/**
 * useBuddyGroupMembers — Member list with join/remove mutations.
 *
 * All member-write operations route through the /buddy-group-ops workflow
 * endpoints, which enforce rodeo eligibility, duplicate detection, size
 * limits, and org-membership checks. CRUD endpoints are only used for
 * read-only member listing.
 *
 * IMPORTANT: The join workflow adds the CURRENT AUTHENTICATED USER —
 * there is no backend endpoint to add a different user on their behalf.
 * The UI uses a self-join model where each user joins the group themselves.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { buddyGroupOpsKeys } from '../api/extensions/useBuddyGroupOps';
import {
  getBuddyGroupMembers,
  removeBuddyGroupMember,
  joinBuddyGroup,
  addMemberToGroup,
  setPrimaryCardholder,
  updateBuddyGroupName,
  deleteBuddyGroup,
  leaveBuddyGroup,
} from '../services/buddy-group.service';
import type { JoinBuddyGroupRequest } from '../types/buddy-groups';

/**
 * Fetch members of a buddy group.
 */
export function useBuddyGroupMembers(groupId: string) {
  return useQuery({
    queryKey: [...buddyGroupOpsKeys.all, 'members', groupId] as const,
    queryFn: () => getBuddyGroupMembers(groupId),
    enabled: !!groupId,
    staleTime: 60 * 1000,
  });
}

/**
 * Mutation hooks for buddy group management.
 *
 * All mutations invalidate relevant caches on success.
 */
export function useBuddyGroupMutations() {
  const qc = useQueryClient();

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: buddyGroupOpsKeys.all });
  };

  /**
   * Join the current user to a buddy group via the workflow endpoint.
   * Routes through /buddy-group-ops/join which enforces all business rules:
   * rodeo eligibility, duplicate detection, size limits, org-membership.
   *
   * NOTE: This adds the CURRENT AUTHENTICATED USER, not an arbitrary user.
   */
  const joinGroup = useMutation({
    mutationFn: (data: JoinBuddyGroupRequest) => joinBuddyGroup(data),
    onSuccess: () => invalidateAll(),
  });

  /**
   * Add a specific user to a buddy group. Only the primary cardholder can
   * call this. Routes through /buddy-group-ops/:groupId/add-member which
   * enforces all business rules server-side.
   */
  const addMember = useMutation({
    mutationFn: ({
      groupId,
      userProfileId,
      firstName,
      lastName,
      dateOfBirth,
    }: {
      groupId: string;
      userProfileId: string;
      firstName?: string;
      lastName?: string;
      dateOfBirth?: string;
    }) => addMemberToGroup(groupId, userProfileId, firstName, lastName, dateOfBirth),
    onSuccess: () => invalidateAll(),
  });

  /**
   * Remove a member from a buddy group via the workflow endpoint.
   * Enforces: only primary can remove, cannot remove self or primary.
   */
  const removeMember = useMutation({
    mutationFn: ({ groupId, memberId }: { groupId: string; memberId: string }) =>
      removeBuddyGroupMember(groupId, memberId),
    onSuccess: () => invalidateAll(),
  });

  /** Transfer primary cardholder role. */
  const transferPrimary = useMutation({
    mutationFn: ({
      groupId,
      userProfileId,
    }: {
      groupId: string;
      userProfileId: string;
    }) => setPrimaryCardholder(groupId, userProfileId),
    onSuccess: () => invalidateAll(),
  });

  /** Rename a buddy group. */
  const renameGroup = useMutation({
    mutationFn: ({
      groupId,
      groupName,
    }: {
      groupId: string;
      groupName: string;
    }) => updateBuddyGroupName(groupId, groupName),
    onSuccess: () => invalidateAll(),
  });

  /**
   * Delete a buddy group. Only the primary cardholder should call this.
   *
   * Deletion cascades through several non-buddy-group caches:
   *   - event_registrations rows lose their buddy_group_id (FK SET NULL +
   *     service-layer unlink) → any rodeo/event page rendering "this is a
   *     buddy-group registration" will be stale until refetched
   *   - buddy_group_rodeo_attachments rows are CASCADE-deleted by the FK
   *     → rodeo card grids showing the group as "registered for X" go
   *     stale
   *
   * Invalidate all four namespaces so every consumer (event detail
   * page, rodeo detail page, my-registrations list, etc.) refetches
   * immediately instead of showing the pre-delete snapshot until the
   * user manually refreshes.
   */
  const deleteGroup = useMutation({
    mutationFn: (groupId: string) => deleteBuddyGroup(groupId),
    onSuccess: () => {
      invalidateAll();
      qc.invalidateQueries({ queryKey: ['buddy-groups'] });
      qc.invalidateQueries({ queryKey: ['buddy-group-rodeo-attachments'] });
      qc.invalidateQueries({ queryKey: ['event-registrations'] });
      qc.invalidateQueries({ queryKey: ['registration-ops'] });
    },
  });

  /** Leave a buddy group (non-primary members only). */
  const leaveGroup = useMutation({
    mutationFn: (groupId: string) => leaveBuddyGroup(groupId),
    onSuccess: () => invalidateAll(),
  });

  return { joinGroup, addMember, removeMember, transferPrimary, renameGroup, deleteGroup, leaveGroup };
}
